import { format, subDays, startOfMonth, endOfMonth, subMonths, startOfQuarter, startOfYear } from "date-fns";

export type PeriodType = "days" | "this_month" | "last_month" | "this_quarter" | "this_year" | "last_year";

export interface PeriodOption {
  value: string;
  label: string;
  periodType: PeriodType;
}

export interface DateRange {
  startDate: Date;
  endDate: Date;
  days: number;
}

export const PERIOD_OPTIONS: PeriodOption[] = [
  { value: "7", label: "Last 7 days", periodType: "days" },
  { value: "14", label: "Last 14 days", periodType: "days" },
  { value: "last_month", label: "Last Month", periodType: "last_month" },
  { value: "this_month", label: "This Month", periodType: "this_month" },
  { value: "this_quarter", label: "This Quarter", periodType: "this_quarter" },
  { value: "this_year", label: "This Year", periodType: "this_year" },
  { value: "last_year", label: "Last Year", periodType: "last_year" },
];

/**
 * Calculates calendar-based date ranges for common period selections.
 * Uses the browser's local timezone for date calculations.
 * 
 * @param periodValue - The period identifier (e.g., "7", "this_month", "last_year")
 * @returns Object containing startDate, endDate, and the number of days in the range
 * 
 * Period types:
 * - "days" (numeric values like "7", "14"): Rolling N days back from today
 * - "this_month": From 1st of current month to today
 * - "last_month": Complete previous calendar month
 * - "this_quarter": From start of current quarter to today
 * - "this_year": From January 1st of current year to today
 * - "last_year": Complete previous calendar year (Jan 1 - Dec 31)
 */
export function getDateRangeForPeriod(periodValue: string): DateRange {
  const today = new Date();
  const option = PERIOD_OPTIONS.find(o => o.value === periodValue);
  const periodType = option?.periodType ?? "days";
  
  switch (periodType) {
    case "this_month": {
      const startDate = startOfMonth(today);
      return { startDate, endDate: today, days: Math.ceil((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1 };
    }
    case "last_month": {
      const lastMonth = subMonths(today, 1);
      const startDate = startOfMonth(lastMonth);
      const endDate = endOfMonth(lastMonth);
      return { startDate, endDate, days: Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1 };
    }
    case "this_quarter": {
      const startDate = startOfQuarter(today);
      return { startDate, endDate: today, days: Math.ceil((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1 };
    }
    case "this_year": {
      const startDate = startOfYear(today);
      return { startDate, endDate: today, days: Math.ceil((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1 };
    }
    case "last_year": {
      const lastYear = new Date(today.getFullYear() - 1, 0, 1);
      const startDate = startOfYear(lastYear);
      const endDate = new Date(today.getFullYear() - 1, 11, 31);
      return { startDate, endDate, days: 365 };
    }
    default: {
      const numDays = parseInt(periodValue, 10) || 30;
      const startDate = subDays(today, numDays - 1);
      return { startDate, endDate: today, days: numDays };
    }
  }
}

/**
 * Formats a date range for display (e.g., "Jan 1 - Jan 28, 2026")
 */
export function formatDateRange(startDate: Date, endDate: Date): string {
  return `${format(startDate, "MMM d")} - ${format(endDate, "MMM d, yyyy")}`;
}

/**
 * Formats a UTC ISO timestamp for display in UTC time.
 * Handles PostgreSQL timestamps that may lack the 'Z' suffix.
 * Output example: "Jun 3, 2026 2:15 PM"
 */
export function formatDateTimeUTC(dateStr: string): string {
  const date = new Date(dateStr.endsWith("Z") ? dateStr : dateStr + "Z");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[date.getUTCMonth()];
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  const hours = date.getUTCHours();
  const mins = date.getUTCMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${month} ${day}, ${year} ${hour12}:${mins} ${ampm}`;
}
